import React, { useState } from 'react';
import { Badge } from '../ui/Badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { Calendar, Star, ChevronRight, MoreVertical, Trash2, Loader2 } from 'lucide-react';
import TalRevAPI, { handleApiError } from '../../services/api';

// Match the API response structure from ApiBasedProfileCard
interface ApiTalentCardData {
  id: string;
  status: string;
  progress: number;
  employee_name: string;
  current_stage: string;
  start_time: string;
  estimated_time_remaining: number;
  error: string | null;
  processing_duration: string | null;
  file_name: string;
  created_by: string;  // Owner email
  user_id: string;     // Owner user_id
  level: string;
  role: string;
  team: string;
  tenure: string;
  manager: string;
  tfcs: string | string[];
  average_score: number;
  total_dimensions: number;
  key_strengths: string[];
  development_areas: string[];
  confidence: number;
  overall_assessment: string;
  processed_date: string;
}

interface TalentListItemProps {
  talentData: ApiTalentCardData;
  onClick?: (talentId: string) => void;
  onDelete?: (talentId: string) => void;
  isAdmin?: boolean;
}

const TalentListItem: React.FC<TalentListItemProps> = ({ talentData, onClick, onDelete, isAdmin = false }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Parse TFCs from API response (same logic as ApiBasedProfileCard)
  const parseTfcs = (tfcs: string | string[]) => {
    if (Array.isArray(tfcs)) {
      return tfcs.join(', ');
    } else if (typeof tfcs === 'string') {
      try {
        const cleanedString = tfcs.replace(/'/g, '"');
        const parsed = JSON.parse(cleanedString);
        return Array.isArray(parsed) ? parsed.join(', ') : tfcs;
      } catch {
        const match = tfcs.match(/\[(.*?)\]/);
        if (match) {
          return match[1].replace(/'/g, '').replace(/"/g, '');
        }
        return tfcs;
      }
    }
    return 'N/A';
  };

  const getLevelColor = (level: string) => {
    if (level.startsWith('L7') || level.startsWith('L8')) {
      return 'bg-purple-50 text-[var(--amazon-orange)] border-purple-200';
    } else if (level.startsWith('L5') || level.startsWith('L6')) {
      return 'bg-blue-50 text-[var(--info-color)] border-blue-200';
    } else {
      return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onDelete) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await TalRevAPI.deleteTalentCard(talentData.id);
      onDelete(talentData.id);
    } catch (error) {
      const errorMessage = handleApiError(error);
      setDeleteError(errorMessage);
      console.error('Failed to delete talent:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
      onClick={() => onClick?.(talentData.id)}
    >
      <div className="flex items-center gap-6 px-4 py-3">
        {/* Name and basic info - Left section */}
        <div className="flex-1 min-w-0" style={{minWidth: '200px'}}>
          <div className="flex items-center gap-3 mb-1">
            <h3 className="text-[var(--font-size-base)] font-[var(--font-weight-semibold)] text-gray-900 truncate">
              {talentData.employee_name}
            </h3>
            <Badge
              variant="outline"
              className={`${getLevelColor(talentData.level)} font-[var(--font-weight-medium)] flex-shrink-0 text-[var(--font-size-xs)]`}
            >
              {talentData.level}
            </Badge>
          </div>

          <div className="text-[var(--font-size-sm)] text-gray-600">{talentData.role}</div>

          <div className="flex items-center gap-3 text-[var(--font-size-sm)] text-gray-500 mt-1">
            <div className="flex items-center gap-1">
              <BuildingOfficeIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{talentData.team || 'Unknown'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{talentData.tenure}</span>
            </div>
            {isAdmin && talentData.created_by && (
              <div className="flex items-center gap-1">
                <span className="text-[var(--font-size-xs)] text-gray-400">•</span>
                <span className="text-[var(--font-size-xs)] text-gray-400">
                  Uploaded by {talentData.created_by.split('@')[0]}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* TFCs and Score - Middle section */}
        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="min-w-0" style={{width: '200px'}}>
            <div className="text-[var(--font-size-xs)] text-gray-500">TFCs</div>
            <div className="text-[var(--font-size-sm)] text-gray-900 truncate">
              {parseTfcs(talentData.tfcs)}
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <Star className="h-3.5 w-3.5 text-[var(--warning-color)] fill-current flex-shrink-0" />
              <span className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)]">
                {talentData.average_score}/5.0
              </span>
            </div>
          </div>

          {/* Right section - Updated date and actions */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-right" style={{width: '100px'}}>
              <div className="text-[var(--font-size-xs)] text-gray-500">Updated</div>
              <div className="text-[var(--font-size-sm)] font-[var(--font-weight-medium)] text-gray-700">
                {new Date(talentData.processed_date).toLocaleDateString()}
              </div>
            </div>

            <div className="flex items-center gap-1">
              {onDelete && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                      disabled={isDeleting}
                    >
                      {isDeleting ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                      ) : (
                        <MoreVertical className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      onClick={handleDelete}
                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Talent
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <ChevronRight className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      {deleteError && (
        <div className="px-4 pb-3">
          <div className="p-2 bg-red-50 border border-red-200 rounded text-[var(--font-size-sm)] text-red-600">
            {deleteError}
          </div>
        </div>
      )}
    </div>
  );
};

export default TalentListItem;